export type AmPm = "" | "AM" | "PM";

export type DateTimeParts = {
  date: string;
  hour: string;
  minute: string;
  ampm: AmPm;
};

export const HOURS_12 = ["12", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11"] as const;
export const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));

export function emptyDateTimeParts(): DateTimeParts {
  return { date: "", hour: "", minute: "", ampm: "" };
}

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

/** Parse legacy `datetime-local` value (YYYY-MM-DDTHH:mm) into split fields. */
export function parseDateTimeLocal(value: string): DateTimeParts {
  if (!value) return emptyDateTimeParts();
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return emptyDateTimeParts();
  const date = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  const h24 = d.getHours();
  const ampm: AmPm = h24 >= 12 ? "PM" : "AM";
  let h12 = h24 % 12;
  if (h12 === 0) h12 = 12;
  return {
    date,
    hour: String(h12),
    minute: pad2(d.getMinutes()),
    ampm,
  };
}

function to24Hour(hour12: number, ampm: AmPm): number {
  if (ampm === "AM") return hour12 === 12 ? 0 : hour12;
  return hour12 === 12 ? 12 : hour12 + 12;
}

/** Minutes since local midnight (0–1439). Requires hour + AM/PM. */
export function partsToMinutesSinceMidnight(
  parts: Pick<DateTimeParts, "hour" | "minute" | "ampm">,
): number | undefined {
  if (!parts.hour || !parts.ampm) return undefined;
  const h = to24Hour(parseInt(parts.hour, 10), parts.ampm);
  const m = parts.minute ? parseInt(parts.minute, 10) : 0;
  return h * 60 + m;
}

function localMinutesSinceMidnight(isoUtc: string): number {
  const d = new Date(isoUtc);
  return d.getHours() * 60 + d.getMinutes();
}

function inTimeOfDayRange(localMin: number, fromMin?: number, toMin?: number): boolean {
  if (fromMin === undefined && toMin === undefined) return true;
  if (fromMin !== undefined && toMin !== undefined) {
    if (fromMin <= toMin) return localMin >= fromMin && localMin <= toMin;
    return localMin >= fromMin || localMin <= toMin;
  }
  if (fromMin !== undefined) return localMin >= fromMin;
  return localMin <= toMin!;
}

/** Client-side time-of-day window when date is not set on that bound. */
export function matchesTimeOfDayWindow(
  isoUtc: string,
  from: DateTimeParts,
  to: DateTimeParts,
): boolean {
  const fromMin =
    !from.date && from.hour && from.ampm ? partsToMinutesSinceMidnight(from) : undefined;
  const toMin = !to.date && to.hour && to.ampm ? partsToMinutesSinceMidnight(to) : undefined;
  if (fromMin === undefined && toMin === undefined) return true;
  return inTimeOfDayRange(localMinutesSinceMidnight(isoUtc), fromMin, toMin);
}

/** Combine split fields to ISO UTC for the search API (requires a calendar date). */
export function partsToIso(parts: DateTimeParts, edge: "start" | "end"): string | undefined {
  if (!parts.date) return undefined;
  const [y, mo, day] = parts.date.split("-").map(Number);
  if (!y || !mo || !day) return undefined;

  let h = 0;
  let m = 0;
  let s = 0;
  let ms = 0;

  if (parts.hour && parts.ampm) {
    h = to24Hour(parseInt(parts.hour, 10), parts.ampm);
    m = parts.minute ? parseInt(parts.minute, 10) : 0;
  } else if (edge === "end") {
    h = 23;
    m = 59;
    s = 59;
    ms = 999;
  }

  const dt = new Date(y, mo - 1, day, h, m, s, ms);
  return Number.isNaN(dt.getTime()) ? undefined : dt.toISOString();
}

export function hasDatePart(parts: DateTimeParts): boolean {
  return Boolean(parts.date);
}

export function hasTimePart(parts: DateTimeParts): boolean {
  return Boolean(parts.hour && parts.ampm);
}

export function hasDateTimeParts(parts: DateTimeParts): boolean {
  return hasDatePart(parts) || hasTimePart(parts);
}

export function readDateTimeParts(
  params: URLSearchParams,
  prefix: "from" | "to",
  legacyKey: "from" | "to",
): DateTimeParts {
  const date = params.get(`${prefix}_date`) ?? "";
  const hour = params.get(`${prefix}_hour`) ?? "";
  const minute = params.get(`${prefix}_minute`) ?? "";
  const ampm = (params.get(`${prefix}_ampm`) ?? "") as AmPm;

  if (date || hour || minute || ampm) {
    return { date, hour, minute, ampm: ampm === "AM" || ampm === "PM" ? ampm : "" };
  }

  const legacy = params.get(legacyKey) ?? "";
  return legacy ? parseDateTimeLocal(legacy) : emptyDateTimeParts();
}

export function writeDateTimeParts(
  params: URLSearchParams,
  prefix: "from" | "to",
  parts: DateTimeParts,
): void {
  if (parts.date) params.set(`${prefix}_date`, parts.date);
  if (parts.hour) params.set(`${prefix}_hour`, parts.hour);
  if (parts.minute) params.set(`${prefix}_minute`, parts.minute);
  if (parts.ampm) params.set(`${prefix}_ampm`, parts.ampm);
}
